import { Dispatch, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { DATE_FORMATS } from "@bps/date-time";
import {
  confirm,
  FontIcon,
  IconButton,
  ShimmeredDetailsListProps,
  Stack,
  Text,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Selector } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useDeleteSelector } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { SelectorRules } from "./SelectorRules";

export interface SelectorsTableProps extends ShimmeredDetailsListProps {
  setSelector: Dispatch<React.SetStateAction<Selector>>;
}

export const SelectorsTable = ({
  setSelector,
  ...props
}: SelectorsTableProps) => {
  const theme = useTheme();
  const columns: DataTableColumn<Selector>[] = useMemo(() => {
    return [
      {
        key: "name",
        name: "Name",
        minWidth: 150,
        maxWidth: 150,
        isResizable: true,
        isSorted: true,
        sort: true,
        onRender: (selector: Selector) => (
          <Stack
            wrap
            styles={{
              root: { whiteSpace: "initial" }
            }}
          >
            <Stack.Item>
              <TooltipHost content="System Managed">
                {selector.isSystemManaged && (
                  <FontIcon
                    iconName="System"
                    styles={{
                      root: {
                        color: theme.palette.orangeLight,
                        fontSize: 18
                      }
                    }}
                  />
                )}{" "}
              </TooltipHost>
              <Text>{selector.name}</Text>
            </Stack.Item>

            <Text variant="small">Description: {selector.description}</Text>
          </Stack>
        )
      },
      {
        key: "rules",
        name: "Rules",
        minWidth: 200,
        maxWidth: 600,
        isResizable: true,
        onRender: (selector: Selector) => <SelectorRules selector={selector} />
      },
      {
        key: "created",
        name: "Created",
        minWidth: 150,
        maxWidth: 150,
        isResizable: true,
        sort: (leftValue, rightValue) => {
          return (
            (leftValue.changeLog?.createdDate?.valueOf() ?? 0) -
            Number(rightValue.changeLog?.createdDate?.valueOf() ?? 0)
          );
        },
        onRender: (selector: Selector) =>
          selector.changeLog?.createdDate?.toFormat(
            DATE_FORMATS.LONG_DATE_TIME_FORMAT
          )
      },
      {
        key: "updated",
        name: "Last Updated",
        minWidth: 150,
        isResizable: true,
        sort: (leftValue, rightValue) => {
          return (
            (leftValue.changeLog?.createdDate?.valueOf() ?? 0) -
            Number(rightValue.changeLog?.createdDate?.valueOf() ?? 0)
          );
        },
        onRender: (selector: Selector) =>
          selector.changeLog?.updatedDate?.toFormat(
            DATE_FORMATS.LONG_DATE_TIME_FORMAT
          )
      },
      {
        key: "deviceCount",
        name: "Device Count",
        minWidth: 110,
        isResizable: true,
        isSorted: true,
        sort: true,
        onRender: (selector: Selector) => selector.deviceCount
      },
      {
        key: "deviceCountRefresh",
        name: "",
        minWidth: 30,
        maxWidth: 30,
        onRender: (selector: Selector) => (
          <SelectorEvaluateButton
            selector={selector}
            setSelector={setSelector}
          />
        ),
        sort: false
      },
      {
        key: "edit",
        name: "",
        minWidth: 30,
        maxWidth: 30,
        onRender: (selector: Selector) => <SelectorEditButton {...selector} />,
        sort: false
      },
      {
        key: "delete",
        name: "",
        minWidth: 30,
        maxWidth: 30,
        onRender: (selector: Selector) => (
          <SelectorDeleteButton {...selector} />
        ),
        sort: false
      }
    ];
  }, [setSelector, theme.palette.orangeLight]);

  return (
    <DataTable
      stickyHeader
      shimmerLines={10}
      columns={columns}
      onShouldVirtualize={() => true}
      {...props}
    />
  );
};

const SelectorEditButton = (selector: Selector) => {
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const navigate = useNavigate();

  return (
    <IconButton
      onClick={() => navigate(selector.id)} // Edit selectors
      styles={{
        root: {
          background: "transparent"
        }
      }}
      disabled={selector.isSystemManaged || !hasWritePermission}
      iconProps={{ iconName: "Edit" }}
      title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
    />
  );
};

interface SelectorEvaluateButtonProps {
  selector: Selector;
  setSelector: Dispatch<React.SetStateAction<Selector>>;
}

const SelectorEvaluateButton = ({
  selector,
  setSelector
}: SelectorEvaluateButtonProps) => {
  const theme = useTheme();

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );
  return (
    <IconButton
      onClick={() => setSelector(selector)}
      styles={{
        root: {
          color: theme.palette.white,
          backgroundColor: theme.palette.themePrimary
        },
        rootHovered: {
          backgroundColor: theme.palette.themeSecondary,
          color: theme.palette.white
        }
      }}
      iconProps={{ iconName: "Rerun" }}
      disabled={!hasWritePermission}
      title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
    />
  );
};

const onDeleteSelector = async () => {
  const isConfirmed = await confirm({
    confirmButtonProps: {
      text: "Delete"
    },
    cancelButtonProps: {
      text: "Cancel"
    },
    dialogContentProps: {
      title: "Delete this selector?",
      subText:
        "Are you sure you want to delete this Selector? Note, the selector must not be assigned to a deployment ring to be deleted successfully."
    }
  });
  return isConfirmed;
};

const SelectorDeleteButton = (selector: Selector) => {
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const { mutateAsync: deleteSelector } = useDeleteSelector();

  return (
    <IconButton
      onClick={async () => {
        if (await onDeleteSelector()) deleteSelector(selector.id);
      }}
      styles={{
        root: {
          background: "transparent",
          color: "red"
        }
      }}
      iconProps={{ iconName: "Cancel" }}
      disabled={!hasWritePermission || selector.isSystemManaged}
      title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
    />
  );
};
